/**
 * declareEnumColumnDialogStore — global open/close state for the
 * Declare-as-Enum dialog. Mirrors `declareJsonColumnDialogStore`: any
 * feature can pop the dialog by calling `openDeclareEnumColumnDialog(…)`
 * without prop-drilling, and a single `GlobalDeclareEnumColumnDialog` mount
 * in `App.tsx` renders it.
 */

import { create } from 'zustand';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { EnumLookupTarget } from '../../../../../types/electron';
import type { TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';

export type DeclareEnumColumnDialogMode =
  | 'native'
  | 'check-constraint'
  | 'sampled'
  | 'eligible'
  /** FK-backed: values + names come from the referenced table (`lookup` source). */
  | 'lookup';

/**
 * Why an FK column can't be declared as a *lookup* enum — surfaced as a muted
 * one-liner in the (plain) declare dialog so the user understands why names
 * aren't on offer. Mirrors `LookupEnumTargetResolution`'s `reason`, minus
 * `'none'` (no FK context at all ⇒ nothing to explain).
 */
export type DeclareEnumColumnLookupIneligibleReason =
  | 'array'
  | 'polymorphic'
  | 'json-path';

/**
 * Which UI surface triggered the dialog. Powers analytics
 * (`feature:enum_declare { trigger }`) and lightly tweaks copy
 * (e.g. eligible-mode mentions the name hint when the trigger is
 * `'header-glyph'`).
 */
export type DeclareEnumColumnDialogTrigger =
  | 'header-glyph'
  | 'filter-chip'
  | 'metainfo-manager'
  | 'json-details-dialog'
  /**
   * The loose-FK editor's "Also treat as enum" checkbox — the one trigger that
   * never opens this dialog. It declares straight from
   * `LooseRelationshipTargetPicker`'s save, and reuses this union so the
   * `feature:enum_declare` trigger property stays one closed set.
   */
  | 'loose-fk-editor'
  /**
   * The enum details dialog's implicit variant: "Declare on this column only"
   * pins a reference-table-driven lookup enum onto the column itself. Like
   * `'loose-fk-editor'`, it declares without ever opening this dialog.
   */
  | 'implicit-details'
  /**
   * The FK-cell popover's "Show names from `<table>`". Like the two above it
   * never opens this dialog — it writes a `mode: 'on-demand'` reference-table
   * flag straight from the popover footer — and reuses this union so the
   * reference-table `trigger` property stays one closed set.
   */
  | 'fk-popover';

export type DeclareEnumColumnDialogContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  /** Engine-specific column data type, e.g. `enum('a','b')`, `varchar(32)`, `int`. */
  dataType?: string;
  engine: DatabaseEngineId;
  mode: DeclareEnumColumnDialogMode;
  trigger: DeclareEnumColumnDialogTrigger;
  /** Optional canonical JSONPath (e.g. `$.status`) — present for JSON-path enum declarations. */
  path?: string;
  /** Present iff `path` is set. Drives scalar-extract vs array-element sampling. Default 'scalar'. */
  pathKind?: 'scalar' | 'array';
  /** Present iff `mode === 'lookup'` — the resolved FK target the key/label
   *  pairs come from (persisted verbatim on the declaration). */
  lookupTarget?: EnumLookupTarget;
  /** Set when the column HAS FK context but can't back a lookup enum; the
   *  dialog stays in its plain mode and explains why. */
  lookupIneligibleReason?: DeclareEnumColumnLookupIneligibleReason;
  /**
   * Host metadata providers (Azure Table, S4 review C, M7): the lookup label
   * column's candidates come from them instead of the SQL metadata IPC.
   */
  metadataEditors?: TableExplorerMetadataEditorProviders;
};

type DeclareEnumColumnDialogState = {
  isOpen: boolean;
  context: DeclareEnumColumnDialogContext | null;
};

type DeclareEnumColumnDialogActions = {
  openDialog: (ctx: DeclareEnumColumnDialogContext) => void;
  /** Switch the open dialog's mode in place — used by lookup mode's
   *  "Use sampled values instead" escape hatch. No-op when closed. */
  setMode: (mode: DeclareEnumColumnDialogMode) => void;
  closeDialog: () => void;
};

type DeclareEnumColumnDialogStore = DeclareEnumColumnDialogState &
  DeclareEnumColumnDialogActions;

export const useDeclareEnumColumnDialogStore =
  create<DeclareEnumColumnDialogStore>((set) => ({
    isOpen: false,
    context: null,
    openDialog: (ctx) => {
      // Same rAF defer as the JSON dialog — lets an in-flight Radix overlay
      // close finish before the Dialog mounts, preventing a stuck
      // `body { pointer-events: none }` on overlapping overlay closes.
      const open = () => set({ isOpen: true, context: ctx });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(open);
      } else {
        setTimeout(open, 0);
      }
    },
    setMode: (mode) =>
      set((state) =>
        state.context ? { context: { ...state.context, mode } } : {},
      ),
    closeDialog: () => set({ isOpen: false, context: null }),
  }));

export const openDeclareEnumColumnDialog = (
  ctx: DeclareEnumColumnDialogContext,
) => useDeclareEnumColumnDialogStore.getState().openDialog(ctx);
